import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Input, message } from 'antd';
import type { TCredentialingProvider, TKlarityPayer } from 'types/credentialing';
import { signGroupAgreement, confirmCredentialing, getCredentialingOverview } from 'api/credentialing';
import { getHelloKlarityHost } from 'utils/common';
import ProviderStore from 'store/Provider';
import SignatureBlock, { type TSignatureRecord, type TSignee } from './SignatureBlock';
import { DEPOSIT, CASHBACK_LIMIT } from '../../constants';
import s from './s.module.less';

const DEPOSIT_DIVISOR = 100;

interface Props {
    /** 押金已被 Ops 减免（CREDENTIALING_FEE_WAIVED）：金额划线、实付 $0 */
    feeWaived?: boolean;
    selectedPayers: string[];
    alreadyCredentialedPayers: string[];
    isGroup?: boolean;
    isAddFlow?: boolean;
    providerIds: number[];
    perProviderPayers?: Record<string, string[]>;
    providers?: TCredentialingProvider[];
    payerOptions: TKlarityPayer[];
    // Fallback payer name/state keyed by klarityPayerId, for payers not present in payerOptions (e.g.
    // the review deep-link, where the historically-selected payers aren't in the current state's
    // active list). Sourced from the group/payers response, which carries payerName/payerState directly.
    payerInfoFallback?: Record<string, { name: string; state: string }>;
    onBack: () => void;
    onConfirm: () => void;
}

const AgreementStep = ({
    feeWaived,
    selectedPayers,
    alreadyCredentialedPayers,
    isGroup,
    isAddFlow,
    providerIds,
    perProviderPayers,
    providers,
    payerOptions,
    payerInfoFallback,
    onBack,
    onConfirm,
}: Props) => {
    const [esignName, setEsignName] = useState('');
    const [esignDate, setEsignDate] = useState(() => {
        const d = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    });
    const [agreeCheck, setAgreeCheck] = useState(false);
    const [authPayerSubmit, setAuthPayerSubmit] = useState(false);
    const [authDataRelease, setAuthDataRelease] = useState(false);
    const [authTruthfulness, setAuthTruthfulness] = useState(false);
    const [groupAttest, setGroupAttest] = useState(false);

    // #14：每个要提交的 provider 都要有自己的签名（solo 就是本人）；提交时按 id 生成快照
    const [getUser] = ProviderStore.useStore();
    const loginId = getUser('data')?.id;
    const signees: TSignee[] = useMemo(
        () =>
            isGroup
                ? providerIds.map((id) => {
                      const p = providers?.find((x) => x.providerId === id);
                      const name = p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : '';
                      return { providerId: id, name: name || p?.email || `Provider ${id}`, self: id === loginId };
                  })
                : [{ providerId: providerIds[0], name: '', self: true }],
        [isGroup, providerIds, providers, loginId],
    );
    const [signatures, setSignatures] = useState<Record<number, TSignatureRecord | null>>({});
    const allSigned = signees.length > 0 && signees.every((p) => !!signatures[p.providerId]?.id);

    const payerMap = useMemo(() => {
        const map: Record<string, TKlarityPayer> = {};
        payerOptions.forEach((p) => {
            map[String(p.id)] = p;
        });
        return map;
    }, [payerOptions]);

    // Display name prefixed with the payer's state, e.g. "CA - Aetna". State comes from the current
    // state's active payerOptions or, for review deep-links, the group/payers fallback.
    const getPayerName = (key: string) => {
        const name = payerMap[key]?.name || payerInfoFallback?.[key]?.name || key;
        const state = payerMap[key]?.state || payerInfoFallback?.[key]?.state || '';
        return state ? `${state} - ${name}` : name;
    };
    const getPayerDeposit = (key: string) => {
        const payer = payerMap[key];
        return payer ? payer.depositCents / DEPOSIT_DIVISOR : DEPOSIT;
    };

    const panelRows = useMemo(() => {
        if (!isGroup || !perProviderPayers || !providers) {
            return selectedPayers.map((key) => {
                const name = getPayerName(key);
                const isAC = alreadyCredentialedPayers.includes(key);
                return {
                    key,
                    name,
                    providerNames: [] as string[],
                    providerCount: 1,
                    isAC,
                    deposit: getPayerDeposit(key),
                };
            });
        }

        const panelProviders: Record<string, string[]> = {};
        const panelOrder: string[] = [];
        Object.entries(perProviderPayers).forEach(([providerId, payers]) => {
            const prov = providers.find((p) => String(p.providerId) === providerId);
            const provName = prov ? `${prov.firstName} ${prov.lastName}` : providerId;
            payers.forEach((payerKey) => {
                if (!panelProviders[payerKey]) {
                    panelProviders[payerKey] = [];
                    panelOrder.push(payerKey);
                }
                panelProviders[payerKey].push(provName);
            });
        });

        return panelOrder.map((key) => {
            const name = getPayerName(key);
            const provNames = panelProviders[key] || [];
            const isAC = alreadyCredentialedPayers.includes(key);
            return {
                key,
                name,
                providerNames: provNames,
                providerCount: provNames.length,
                isAC,
                deposit: getPayerDeposit(key),
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGroup, perProviderPayers, providers, selectedPayers, alreadyCredentialedPayers, payerMap]);

    const { totalDeposit, depositCount } = useMemo(() => {
        let total = 0;
        let count = 0;
        panelRows.forEach((row) => {
            if (!row.isAC) {
                const n = isGroup ? row.providerCount : 1;
                total += row.deposit * n;
                count += n;
            }
        });
        return { totalDeposit: total, depositCount: count };
    }, [panelRows, isGroup]);

    const cbCount = Math.min(depositCount, CASHBACK_LIMIT);
    const cbAmount = cbCount * DEPOSIT;

    const dateStr = useMemo(() => {
        const d = new Date();
        return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }, []);

    const [submitting, setSubmitting] = useState(false);

    const canConfirm =
        agreeCheck &&
        authPayerSubmit &&
        authDataRelease &&
        authTruthfulness &&
        esignName.trim() &&
        esignDate &&
        allSigned &&
        (!isGroup || groupAttest);

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            let res;
            if (isGroup) {
                res = await signGroupAgreement({
                    completePractice: !isAddFlow,
                    esignatureName: esignName.trim(),
                    providerIds,
                    signedDate: esignDate,
                    providerSignatures: signees.map((p) => ({
                        providerId: p.providerId,
                        signatureId: signatures[p.providerId]!.id as number,
                    })),
                });
            } else {
                const klarityPayerIds = selectedPayers
                    .filter((k) => !alreadyCredentialedPayers.includes(k))
                    .map(Number);
                res = await confirmCredentialing(
                    {
                        providerId: providerIds[0],
                        klarityPayerIds,
                        esignatureName: esignName.trim(),
                        signedDate: esignDate,
                        signatureId: signatures[providerIds[0]]?.id,
                    },
                    undefined,
                    true,
                );
            }
            if (res && !res.error) {
                onConfirm();
            } else {
                if (!isGroup) {
                    try {
                        const overview = await getCredentialingOverview();
                        const provider = overview?.data?.data?.providers?.[0];
                        if (provider?.onboardingStatus === 'COMPLETED') {
                            onConfirm();
                            return;
                        }
                    } catch (_) {}
                }
                message.error(res?.error || 'Failed to sign agreement');
            }
        } catch (e) {
            console.error(e);
            message.error('Failed to sign agreement');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className={s.modalBody}>
                {depositCount > 0 && (
                    <div className={s.summaryBanner}>
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            style={{ flexShrink: 0, marginTop: 1 }}
                        >
                            <circle cx="10" cy="10" r="10" fill="#16A34A" />
                            <path
                                d="M6 10.5l2.5 2.5L14 7.5"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        {/* V2：cashback 金额行加粗，避免折扣被忽略；押金减免时没有 cashback 可言 */}
                        {feeWaived ? (
                            <span>
                                <strong>No deposit needed.</strong> Your credentialing fee is waived by Klarity.
                            </span>
                        ) : (
                            <span>
                                <strong>
                                    {cbCount} of your {depositCount} deposit{depositCount > 1 ? 's' : ''} (${cbAmount})
                                </strong>{' '}
                                will be returned as cashback after your first insurance claim is submitted to Klarity.
                            </span>
                        )}
                    </div>
                )}

                <div className={s.agPayerList}>
                    {panelRows.map((row) => {
                        let feeLabel = `$${row.deposit} deposit`;
                        if (row.isAC) {
                            feeLabel = '$0';
                        } else if (isGroup && row.providerCount > 1) {
                            feeLabel = `$${row.deposit * row.providerCount} deposit (${row.providerCount} × $${row.deposit})`;
                        }

                        return (
                            <div key={row.key} className={s.agPayerRow}>
                                <span className={s.agPayerName}>
                                    {row.name}
                                    {row.providerNames.length > 0 && (
                                        <span className={s.agPayerProviders}>({row.providerNames.join(', ')})</span>
                                    )}
                                    {row.isAC && <span className={s.alreadyTag}>Already credentialed</span>}
                                </span>
                                <span
                                    className={`${s.agPayerFee} ${row.isAC ? s.agPayerFeeFree : ''} ${
                                        feeWaived && !row.isAC ? s.struck : ''
                                    }`}
                                >
                                    {feeLabel}
                                </span>
                            </div>
                        );
                    })}
                </div>

                <div className={s.agTotalRow}>
                    <span>Total deposits</span>
                    <span>
                        <span className={feeWaived ? s.struck : ''}>${totalDeposit}</span>
                        {feeWaived && <strong className={s.waivedTotal}>$0</strong>}
                    </span>
                </div>

                <div className={s.noticeBanner}>
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                        <circle cx="10" cy="10" r="10" fill="#0E62F4" />
                        <path
                            d="M10 9v5"
                            stroke="#fff"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <circle cx="10" cy="6.25" r="1.1" fill="#fff" />
                    </svg>
                    <span>
                        <strong>Before you submit:</strong> A few payers have their own requirements before they&apos;ll
                        accept a submission, such as a minimum number of providers or an extra registration step. These
                        can affect timing. We&apos;ll email you if any apply to you.
                    </span>
                </div>

                <div className={s.esignRow}>
                    <div className={s.esignField}>
                        <label className={s.esignLabel}>E-signature (type your full legal name)</label>
                        <Input
                            placeholder="e.g. Sarah M. Johnson"
                            value={esignName}
                            onChange={(e) => setEsignName(e.target.value)}
                        />
                    </div>
                    <div className={s.esignDateField}>
                        <label className={s.esignLabel}>Date</label>
                        <Input type="date" value={esignDate} onChange={(e) => setEsignDate(e.target.value)} />
                    </div>
                </div>

                <SignatureBlock signees={signees} group={!!isGroup} onChange={setSignatures} />

                <label className={s.agreeRow}>
                    <Checkbox checked={agreeCheck} onChange={(e) => setAgreeCheck(e.target.checked)} />
                    <span>
                        By checking this box, I agree to the{' '}
                        <a
                            href={`${getHelloKlarityHost()}/provider-terms-conditions`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={s.link}
                        >
                            Klarity Health Provider Agreement
                        </a>{' '}
                        on {dateStr}.
                    </span>
                </label>
                <label className={s.agreeRow}>
                    <Checkbox checked={authPayerSubmit} onChange={(e) => setAuthPayerSubmit(e.target.checked)} />
                    <span>
                        I authorize Klarity to submit my credentialing application to the payers I selected, and to
                        access and update my CAQH profile on my behalf.
                    </span>
                </label>
                <label className={s.agreeRow}>
                    <Checkbox checked={authDataRelease} onChange={(e) => setAuthDataRelease(e.target.checked)} />
                    <span>I authorize payers to access and verify the information provided.</span>
                </label>
                <label className={s.agreeRow}>
                    <Checkbox checked={authTruthfulness} onChange={(e) => setAuthTruthfulness(e.target.checked)} />
                    <span>
                        I attest that all information provided is true, complete, and accurate to the best of my
                        knowledge.
                    </span>
                </label>
                {isGroup && (
                    <label className={s.agreeRow}>
                        <Checkbox checked={groupAttest} onChange={(e) => setGroupAttest(e.target.checked)} />
                        <span>
                            I confirm that I have reviewed the information for all providers in my practice and that it
                            is accurate and complete to the best of my knowledge.
                        </span>
                    </label>
                )}
            </div>

            <div className={s.modalFooter}>
                <Button
                    type="primary"
                    block
                    size="large"
                    className={s.primaryBtn}
                    disabled={!canConfirm}
                    loading={submitting}
                    onClick={handleConfirm}
                >
                    Confirm
                </Button>
                <Button block size="large" className={s.backBtn} onClick={onBack}>
                    Back
                </Button>
            </div>
        </>
    );
};

export default AgreementStep;
